/**
 * Health Check Utilities
 *
 * Utilities for detecting and handling health check requests from Kubernetes,
 * Google App Engine, and other infrastructure monitoring systems.
 */

/** Labels / tokens matched as whole labels or hyphen-bounded segments (not substring matches). */
const HEALTH_HOST_LABELS = ['health', 'healthz', 'alive', 'ready', 'ping', 'status', 'heartbeat', '_ah'] as const

const RE_IPV6_FULL = /^([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}$/i
const RE_IPV6_COMPRESSED = /^([0-9a-f]{1,4}:)*::([0-9a-f]{1,4}:)*[0-9a-f]{1,4}$/i

function isIpv4Octets(value: string): boolean {
  const parts = value.split('.')
  if (parts.length !== 4) {
    return false
  }
  for (const part of parts) {
    if (part.length === 0 || part.length > 3 || (part.length > 1 && part.startsWith('0'))) {
      return false
    }
    let n = 0
    for (let i = 0; i < part.length; i++) {
      const c = part.codePointAt(i) ?? 0
      if (c < 48 || c > 57) {
        return false
      }
      n = n * 10 + (c - 48)
    }
    if (n > 255) {
      return false
    }
  }
  return true
}

function isIpv6Address(value: string): boolean {
  if (RE_IPV6_FULL.test(value)) {
    return true
  }
  if (value === '::1' || value === '::') {
    return true
  }
  return RE_IPV6_COMPRESSED.test(value)
}

/**
 * True when `host` looks like a literal IPv4 or IPv6 (zone IDs `%…` are not treated as IPs here).
 */
export function isIpAddress(host: string): boolean {
  if (!host || typeof host !== 'string') {
    return false
  }
  const trimmed = host.trim()
  if (!trimmed || trimmed.includes('%')) {
    return false
  }

  if (trimmed.startsWith('[')) {
    const end = trimmed.indexOf(']')
    if (end < 2) {
      return false
    }
    const inner = trimmed.slice(1, end)
    return isIpv4Octets(inner) || isIpv6Address(inner)
  }

  if (isIpv4Octets(trimmed) || isIpv6Address(trimmed)) {
    return true
  }

  const beforeFirstColon = trimmed.split(':')[0] ?? ''
  return beforeFirstColon.length > 0 && isIpv4Octets(beforeFirstColon)
}

function labelMatchesHealthKeyword(labelLower: string, pattern: string): boolean {
  if (pattern === '_ah') {
    return labelLower === '_ah' || labelLower.includes('_ah')
  }
  return (
    labelLower === pattern ||
    labelLower.startsWith(`${pattern}-`) ||
    labelLower.endsWith(`-${pattern}`) ||
    labelLower.includes(`-${pattern}-`)
  )
}

function hostnameLabelsMatchHealthPatterns(hostnameLower: string): boolean {
  const labels = hostnameLower.split('.').filter(Boolean)
  return HEALTH_HOST_LABELS.some(pattern => labels.some(label => labelMatchesHealthKeyword(label, pattern)))
}

/**
 * True for probe-style hosts: literal IP or hostname whose labels match known health tokens.
 */
export function isHealthCheckRequest(host?: string): boolean {
  if (!host || typeof host !== 'string') {
    return false
  }
  if (isIpAddress(host)) {
    return true
  }
  const hostnameLower = host.toLowerCase().split(':')[0] ?? ''
  return hostnameLower.length > 0 && hostnameLabelsMatchHealthPatterns(hostnameLower)
}

/**
 * Path prefixes that should bypass shared Nitro server middleware (logger, auth).
 * Match the inline allow-lists previously duplicated in `libs/middleware/00.logger.ts`
 * and `libs/middleware/auth.ts`.
 *
 * `/__nuxt` is included here so the logger gains parity with auth, eliminating drift.
 */
const MIDDLEWARE_SKIP_PREFIXES = ['/_nuxt/', '/__nuxt', '/api/warmup', '/api/_ah/warmup'] as const

/** Paths matched exactly (no prefix semantics). Mirrors the existing `url === '/_ah/warmup'` check. */
const MIDDLEWARE_SKIP_EXACT_PATHS: ReadonlySet<string> = new Set(['/_ah/warmup'])

/**
 * True when the incoming request URL is a Nuxt static/internal path or an infrastructure
 * health/warmup probe and should bypass shared server middleware (request logging, auth).
 *
 * Centralizes the allow-list previously duplicated across `libs/middleware/00.logger.ts`
 * and `libs/middleware/auth.ts`. The exact-match entry for `/_ah/warmup` stays in this
 * shared list because multiple apps (for example `apps/clientportal`, `apps/gokollab`)
 * expose that path via Nitro routes or plugins that answer probes before middleware; any
 * request that still reaches shared middleware must match the same allow-list semantics.
 */
export function shouldSkipServerMiddleware(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false
  }
  if (MIDDLEWARE_SKIP_EXACT_PATHS.has(url)) {
    return true
  }
  return MIDDLEWARE_SKIP_PREFIXES.some(prefix => url.startsWith(prefix))
}

/**
 * True when `Host` is empty, a probe IP, a health hostname, or a typical internal
 * upstream (e.g. K8s DNS name without dots). Clientportal ssl-proxy sets the real
 * browser hostname on the `domain` header when `Host` is the internal service name.
 */
function isLikelyInternalRequestHost(host: string): boolean {
  const trimmed = host?.trim() ?? ''
  if (!trimmed) {
    return true
  }
  if (isHealthCheckRequest(trimmed)) {
    return true
  }
  const name = trimmed.split(':')[0].toLowerCase()
  if (!name) {
    return true
  }
  if (name.includes('.svc.') || name.endsWith('.svc.cluster.local')) {
    return true
  }
  // e.g. `clientportal-preview-cache-api-service:6050` — not a public internet hostname
  if (!name.includes('.') && name !== 'localhost') {
    return true
  }
  return false
}

/**
 * Resolves the hostname used for portal-settings / location by domain.
 *
 * Prefers the edge `domain` header (comma-separated → first entry) when present, not a health
 * hostname, and not an internal upstream host shape, then falls back to `Host` when it looks like
 * a public hostname, and finally to raw `Host` (e.g. pod IP when no usable `domain`). Trusts nginx to set `domain`
 * from the original browser host when `Host` is an internal upstream. Does not read `X-Forwarded-Host`.
 */
export function resolvePortalSettingsHost(host: string, domain?: string): string {
  const trimmedHost = host?.trim() ?? ''
  const edgeDomain = domain?.split(',')[0]?.trim() ?? ''

  if (edgeDomain && !isHealthCheckRequest(edgeDomain) && !isLikelyInternalRequestHost(edgeDomain)) {
    return edgeDomain
  }

  if (trimmedHost && !isLikelyInternalRequestHost(trimmedHost)) {
    return trimmedHost
  }

  return trimmedHost
}
